import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../services/axiosInstance';
import Navbar from '../../components/Navbar';

function AdminEstablishmentsManagement() {
  const [establishments, setEstablishments] = useState([]);
  const [filteredEstablishments, setFilteredEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  useEffect(() => {
    fetchEstablishments();
  }, []);

  useEffect(() => {
    filterEstablishments();
  }, [establishments, searchTerm, filterStatus]);

  const fetchEstablishments = async () => {
    try {
      const response = await axiosInstance.get('/admin/establishments');
      setEstablishments(response.data.establishments || []);
      
      // Calculer les statistiques
      const total = response.data.establishments.length;
      const active = response.data.establishments.filter(est => est.visible).length;
      const inactive = total - active;
      
      setStats({ total, active, inactive });
    } catch (error) {
      console.error('Erreur lors du chargement des établissements:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEstablishments = () => {
    let filtered = establishments;

    // Filtrage par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(est => 
        est.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        est.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        est.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrage par statut
    if (filterStatus === 'active') {
      filtered = filtered.filter(est => est.visible);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(est => !est.visible);
    }

    setFilteredEstablishments(filtered);
  };

  const toggleEstablishmentStatus = async (establishmentId, currentStatus) => {
    try {
      await axiosInstance.put(`/admin/establishments/${establishmentId}/toggle-status`, {
        visible: !currentStatus
      });
      
      // Mettre à jour la liste locale
      setEstablishments(prev => prev.map(est => 
        est.id === establishmentId 
          ? { ...est, visible: !currentStatus }
          : est
      ));
      
      // Recalculer les stats
      const updatedEstablishments = establishments.map(est => 
        est.id === establishmentId 
          ? { ...est, visible: !currentStatus }
          : est
      );
      
      const total = updatedEstablishments.length;
      const active = updatedEstablishments.filter(est => est.visible).length;
      const inactive = total - active;
      
      setStats({ total, active, inactive });
      
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      alert('Erreur lors de la modification du statut');
    }
  };

  const styles = {
    container: {
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    header: {
      backgroundColor: '#ffffff',
      padding: '32px',
      borderBottom: '1px solid #e2e8f0',
      marginBottom: '32px'
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    title: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 8px 0'
    },
    subtitle: {
      fontSize: '16px',
      color: '#64748b',
      margin: 0
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 32px'
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '32px'
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      textAlign: 'center'
    },
    statNumber: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 8px 0'
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b',
      margin: 0
    },
    filtersContainer: {
      backgroundColor: '#ffffff',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '24px'
    },
    filtersRow: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    searchInput: {
      flex: 1,
      minWidth: '300px',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px'
    },
    filterSelect: {
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      backgroundColor: '#ffffff'
    },
    establishmentsContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflow: 'hidden'
    },
    establishmentCard: {
      padding: '20px 24px',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'background-color 0.2s'
    },
    establishmentInfo: {
      flex: 1
    },
    establishmentName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '4px'
    },
    establishmentDetails: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '2px'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500',
      marginRight: '12px'
    },
    statusActive: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    statusInactive: {
      backgroundColor: '#fef2f2',
      color: '#dc2626'
    },
    actionsContainer: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      textDecoration: 'none',
      display: 'inline-block',
      textAlign: 'center',
      transition: 'all 0.2s'
    },
    primaryButton: {
      backgroundColor: '#059669',
      color: '#ffffff'
    },
    secondaryButton: {
      backgroundColor: '#f1f5f9',
      color: '#374151',
      border: '1px solid #d1d5db'
    },
    dangerButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff'
    },
    successButton: {
      backgroundColor: '#059669',
      color: '#ffffff'
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '80px 20px',
      fontSize: '16px',
      color: '#64748b'
    },
    emptyState: {
      textAlign: 'center',
      padding: '80px 20px'
    },
    emptyIcon: {
      fontSize: '64px',
      marginBottom: '16px'
    },
    emptyTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    },
    emptyDescription: {
      fontSize: '16px',
      color: '#6b7280'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Navbar />
        <div style={styles.loadingContainer}>
          <div>Chargement des établissements...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Navbar />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>Gestion des Établissements</h1>
          <p style={styles.subtitle}>
            Gérez tous les établissements de la plateforme
          </p>
        </div>
      </div>

      <div style={styles.content}>
        {/* Statistiques */}
        <div style={styles.statsContainer}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats.total}</div>
            <div style={styles.statLabel}>Total Établissements</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statNumber, color: '#059669' }}>{stats.active}</div>
            <div style={styles.statLabel}>Actifs</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statNumber, color: '#dc2626' }}>{stats.inactive}</div>
            <div style={styles.statLabel}>Inactifs</div>
          </div>
        </div>

        {/* Filtres */}
        <div style={styles.filtersContainer}>
          <div style={styles.filtersRow}>
            <input
              type="text"
              placeholder="Rechercher un établissement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">Tous les établissements</option>
              <option value="active">Actifs uniquement</option>
              <option value="inactive">Inactifs uniquement</option>
            </select>
          </div>
        </div>

        {/* Liste des établissements */}
        <div style={styles.establishmentsContainer}>
          {filteredEstablishments.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🏪</div>
              <h3 style={styles.emptyTitle}>Aucun établissement trouvé</h3>
              <p style={styles.emptyDescription}>
                {searchTerm || filterStatus !== 'all' 
                  ? 'Aucun établissement ne correspond à vos critères de recherche.'
                  : 'Aucun établissement n\'est encore enregistré sur la plateforme.'
                }
              </p>
            </div>
          ) : (
            filteredEstablishments.map((establishment, index) => (
              <div
                key={establishment.id}
                style={{
                  ...styles.establishmentCard,
                  borderBottom: index === filteredEstablishments.length - 1 ? 'none' : '1px solid #e2e8f0'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
              >
                <div style={styles.establishmentInfo}>
                  <div style={styles.establishmentName}>{establishment.name}</div>
                  <div style={styles.establishmentDetails}>
                    📍 {establishment.address}, {establishment.city}
                  </div>
                  <div style={styles.establishmentDetails}>
                    📧 {establishment.email} | 📞 {establishment.phone}
                  </div>
                  <div style={styles.establishmentDetails}>
                    ID: {establishment.id} | Créé le: {new Date(establishment.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                
                <div style={styles.actionsContainer}>
                  <span
                    style={{
                      ...styles.statusBadge,
                      ...(establishment.visible ? styles.statusActive : styles.statusInactive)
                    }}
                  >
                    {establishment.visible ? '✅ Actif' : '❌ Inactif'}
                  </span>
                  
                  <Link
                    to={`/admin/${establishment.id}`}
                    style={{ ...styles.button, ...styles.primaryButton }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
                  >
                    Gérer
                  </Link>
                  
                  <button
                    onClick={() => toggleEstablishmentStatus(establishment.id, establishment.visible)}
                    style={{
                      ...styles.button,
                      ...(establishment.visible ? styles.dangerButton : styles.successButton)
                    }}
                    onMouseEnter={(e) => {
                      if (establishment.visible) {
                        e.target.style.backgroundColor = '#b91c1c';
                      } else {
                        e.target.style.backgroundColor = '#047857';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (establishment.visible) {
                        e.target.style.backgroundColor = '#dc2626';
                      } else {
                        e.target.style.backgroundColor = '#059669';
                      }
                    }}
                  >
                    {establishment.visible ? 'Désactiver' : 'Activer'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminEstablishmentsManagement;